/**
 * ExampleIsland - React Island Component Example
 *
 * This is an example component demonstrating the "React Islands" pattern for Shopify themes.
 * It's a basic add to cart flow for a product using Headless UI components.
 *
 * To test, just put `{% render 'react-example' %}` in a section on a product page.
 */

import React, { useState, Fragment } from "react";
import { Listbox, Transition, Dialog } from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

interface Variant {
  id: number;
  title: string;
  price: number;
}

interface ExampleIslandProps {
  productId?: string;
  variants?: Variant[];
  basePrice?: number;
  currency?: string;
}

export const ExampleIslandConfig = {
  key: "example",
  component: ExampleIsland,
};

export default function ExampleIsland({
  productId,
  variants = [],
  basePrice = 0,
  currency = "USD",
}: ExampleIslandProps) {
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(
    variants.length > 0 ? variants[0] : null
  );
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const addToCart = async () => {
    if (!selectedVariant) {
      console.error("No variant selected");
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: selectedVariant.id,
          quantity: quantity,
        }),
      });
      if (!res.ok) throw new Error("Add to cart failed");

      // Show success modal
      setShowSuccessModal(true);

      // Refresh cart and open cart drawer
      refreshCartAndOpenDrawer();
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshCartAndOpenDrawer = () => {
    // First refresh the cart contents
    if (window.routes && window.routes.cart_url) {
      fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const selectors = ["cart-drawer-items", ".cart-drawer__footer"];

          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }

          // After refreshing cart contents, open the cart drawer
          const cartDrawer = document.querySelector("cart-drawer") as any;
          if (cartDrawer && typeof cartDrawer.open === "function") {
            cartDrawer.open();
          }
        })
        .catch((error) => {
          console.error("Error refreshing cart:", error);
          // Fallback: just try to open the cart drawer
          const cartDrawer = document.querySelector("cart-drawer") as any;
          if (cartDrawer && typeof cartDrawer.open === "function") {
            cartDrawer.open();
          }
        });
    } else {
      // Fallback: just try to open the cart drawer
      const cartDrawer = document.querySelector("cart-drawer") as any;
      if (cartDrawer && typeof cartDrawer.open === "function") {
        cartDrawer.open();
      }
    }
  };

  if (variants.length === 0) {
    return (
      <div className="xp-4 xtext-center xtext-gray-500">
        No variants available
      </div>
    );
  }

  return (
    <>
      <div
        className="
        xp-6
        xbg-white
        xrounded-lg
        xshadow-md
        xmax-w-md
        xmx-auto
      "
      >
        <h2
          className="
          xtext-2xl
          xfont-bold
          xtext-gray-800
          xmb-6
          xtext-center
        "
        >
          Add to Cart
        </h2>

        {/* Variant Selection with Headless UI Listbox */}
        <div className="xmb-6">
          <Listbox value={selectedVariant} onChange={setSelectedVariant}>
            <div className="xrelative">
              <Listbox.Label
                className="
                xblock
                xtext-sm
                xfont-medium
                xtext-gray-700
                xmb-2
              "
              >
                Select Variant
              </Listbox.Label>
              <Listbox.Button
                className="
                xrelative
                xw-full
                xcursor-default
                xrounded-lg
                xbg-white
                xpy-2
                xpl-3
                xpr-10
                xtext-left
                xshadow-md
                xfocus:outline-none
                xfocus-visible:ring-2
                xfocus-visible:ring-green
                xfocus-visible:ring-opacity-75
                xfocus-visible:ring-offset-2
                xfocus-visible:ring-offset-green
                xborder
                xborder-gray-300
              "
              >
                <span className="xblock xtruncate">
                  {selectedVariant ? (
                    <>
                      {selectedVariant.title} - {currency}{" "}
                      {(selectedVariant.price / 100).toFixed(2)}
                    </>
                  ) : (
                    "Select a variant"
                  )}
                </span>
                <span
                  className="
                  xpointer-events-none
                  xabsolute
                  xinset-y-0
                  xright-0
                  xflex
                  xitems-center
                  xpr-2
                "
                >
                  <ChevronUpDownIcon
                    className="xh-5 xw-5 xtext-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="xtransition xeasy-in xduration-100"
                leaveFrom="xopacity-100"
                leaveTo="xopacity-0"
              >
                <Listbox.Options
                  className="
                  xabsolute
                  xz-10
                  xmt-1
                  xmax-h-60
                  xw-full
                  xoverflow-auto
                  xrounded-md
                  xbg-white
                  xpy-1
                  xtext-base
                  xshadow-lg
                  xring-1
                  xring-black
                  xring-opacity-5
                  xfocus:outline-none
                  xsm:text-sm
                "
                >
                  {variants.map((variant) => (
                    <Listbox.Option
                      key={variant.id}
                      className={({ active }) =>
                        `xrelative xcursor-default xselect-none xpy-2 xpl-10 xpr-4 ${
                          active ? "xbg-green xtext-white" : "xtext-gray-900"
                        }`
                      }
                      value={variant}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={`xblock xtruncate ${
                              selected ? "xfont-medium" : "xfont-normal"
                            }`}
                          >
                            {variant.title} - {currency}{" "}
                            {(variant.price / 100).toFixed(2)}
                          </span>
                          {selected ? (
                            <span
                              className={`xabsolute xinset-y-0 xleft-0 xflex xitems-center xpl-3 ${
                                active ? "xtext-white" : "xtext-green"
                              }`}
                            >
                              <CheckIcon
                                className="xh-5 xw-5"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        </div>

        {/* Quantity Selection */}
        <div className="xmb-6">
          <label
            htmlFor="quantity"
            className="
              xblock
              xtext-sm
              xfont-medium
              xtext-gray-700
              xmb-2
            "
          >
            Quantity
          </label>
          <input
            id="quantity"
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
            className="
              xw-full
              xpx-3
              xpy-2
              xborder
              xborder-gray-300
              xrounded-md
              xfocus:outline-none
              xfocus:ring-2
              xfocus:ring-green
              xfocus:border-transparent
            "
          />
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={addToCart}
          disabled={isLoading || !selectedVariant}
          className="
            xw-full
            xbg-green
            xtext-white
            xpy-3
            xpx-6
            xrounded-lg
            xfont-bold
            xfont-grotesque
            xuppercase
            xtracking-wide
            xhover:bg-dark-green
            xfocus:outline-none
            xfocus:ring-2
            xfocus:ring-green
            xfocus:ring-offset-2
            xdisabled:opacity-50
            xdisabled:cursor-not-allowed
            xtransition-colors
            xduration-200
          "
        >
          {isLoading ? "Adding..." : "Add to Cart"}
        </button>
      </div>

      {/* Success Modal with Headless UI Dialog */}
      <Transition appear show={showSuccessModal} as={Fragment}>
        <Dialog
          as="div"
          className="xrelative xz-50"
          onClose={() => setShowSuccessModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="xease-out xduration-300"
            enterFrom="xopacity-0"
            enterTo="xopacity-100"
            leave="xease-in xduration-200"
            leaveFrom="xopacity-100"
            leaveTo="xopacity-0"
          >
            <div className="xfixed xinset-0 xbg-black xbg-opacity-25" />
          </Transition.Child>

          <div className="xfixed xinset-0 xoverflow-y-auto">
            <div
              className="
              xflex
              xmin-h-full
              xitems-center
              xjustify-center
              xp-4
              xtext-center
            "
            >
              <Transition.Child
                as={Fragment}
                enter="xease-out xduration-300"
                enterFrom="xopacity-0 xscale-95"
                enterTo="xopacity-100 xscale-100"
                leave="xease-in xduration-200"
                leaveFrom="xopacity-100 xscale-100"
                leaveTo="xopacity-0 xscale-95"
              >
                <Dialog.Panel
                  className="
                  xw-full
                  xmax-w-md
                  xtransform
                  xoverflow-hidden
                  xrounded-2xl
                  xbg-white
                  xp-6
                  xtext-left
                  xalign-middle
                  xshadow-xl
                  xtransition-all
                "
                >
                  <div className="xflex xitems-center xjustify-between xmb-4">
                    <Dialog.Title
                      as="h3"
                      className="
                      xtext-lg
                      xfont-medium
                      xleading-6
                      xtext-gray-900
                    "
                    >
                      Added to Cart!
                    </Dialog.Title>
                    <button
                      onClick={() => setShowSuccessModal(false)}
                      className="
                        xrounded-full
                        xp-1
                        xhover:bg-gray-100
                        xtransition-colors
                      "
                    >
                      <XMarkIcon className="xh-5 xw-5 xtext-gray-400" />
                    </button>
                  </div>

                  <div className="xmb-4">
                    <p className="xtext-sm xtext-gray-500">
                      {selectedVariant?.title} has been added to your cart.
                    </p>
                  </div>

                  <div className="xflex xgap-3">
                    <button
                      onClick={() => setShowSuccessModal(false)}
                      className="
                        xflex-1
                        xrounded-md
                        xbg-gray-100
                        xpx-3
                        xpy-2
                        xtext-sm
                        xfont-medium
                        xtext-gray-700
                        xhover:bg-gray-200
                        xfocus:outline-none
                        xfocus:ring-2
                        xfocus:ring-green
                        xfocus:ring-offset-2
                      "
                    >
                      Continue Shopping
                    </button>
                    <button
                      onClick={() => {
                        setShowSuccessModal(false);
                        // Open cart drawer
                        const cartDrawer = document.querySelector(
                          "cart-drawer"
                        ) as any;
                        if (
                          cartDrawer &&
                          typeof cartDrawer.open === "function"
                        ) {
                          cartDrawer.open();
                        }
                      }}
                      className="
                        xflex-1
                        xrounded-md
                        xbg-green
                        xpx-3
                        xpy-2
                        xtext-sm
                        xfont-medium
                        xtext-white
                        xhover:bg-dark-green
                        xfocus:outline-none
                        xfocus:ring-2
                        xfocus:ring-green
                        xfocus:ring-offset-2
                      "
                    >
                      View Cart
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
